import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  XMarkIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { trainingSessionsAPI, trainingGroupsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../notifications/NotificationProvider';
import { useDashboardRefetch } from '../../hooks/useDashboard';

const ImportSessionsModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const { invalidateDashboard } = useDashboardRefetch();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState(1); // 1: JSON Input, 2: Crew Assignment, 3: Session Review & Edit
  const [showInstructions, setShowInstructions] = useState(true);
  const [jsonInput, setJsonInput] = useState('');
  const [validationResults, setValidationResults] = useState(null);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [selectedCrews, setSelectedCrews] = useState([]);
  const [editingSessions, setEditingSessions] = useState({});
  const [copySuccess, setCopySuccess] = useState(false);
  const [sessionCrewOverrides, setSessionCrewOverrides] = useState({}); // Per-session crew assignments
  const [conflictResults, setConflictResults] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const revalidationTimeoutRef = useRef(null);
  const editingSessionsRef = useRef({});

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (revalidationTimeoutRef.current) {
        clearTimeout(revalidationTimeoutRef.current);
      }
    };
  }, []);

  // Keep the ref in sync with the state
  useEffect(() => {
    editingSessionsRef.current = editingSessions;
  }, [editingSessions]);

  // Get user's crews for assignment
  const {
    data: userTrainingGroups = [],
    isLoading: loadingTrainingGroups,
    error: trainingGroupsError,
  } = useQuery(['userTrainingGroups', 'import'], () => trainingGroupsAPI.getGroups(true), {
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const validateImportMutation = useMutation(
    (importData) => trainingSessionsAPI.validateImportData(importData, selectedCrews),
    {
      onSuccess: (results) => {
        setValidationResults(results);
        setConflictResults(results); // Set conflicts from validation results
        // Pre-select all valid sessions
        setSelectedSessions(results.valid_sessions.map((s) => s.index));

        setCurrentStep(3); // Go to review step after validation
        setShowInstructions(false); // Hide instructions after validation
      },
      onError: (error) => {
        showError('Failed to validate import data. Please check your JSON format.');
        console.error('Validation error:', error);
      },
    }
  );

  const bulkCreateMutation = useMutation(
    (importRequest) => trainingSessionsAPI.bulkCreateSessions(importRequest),
    {
      onSuccess: (results) => {
        queryClient.invalidateQueries('trainingSessions');
        invalidateDashboard(); // Also invalidate dashboard
        showSuccess(`Successfully imported ${results.total_created} training sessions`);
        onClose();
        resetForm();
      },
      onError: (error) => {
        showError('Failed to create sessions. Please try again.');
        console.error('Creation error:', error);
      },
    }
  );

  const resetForm = () => {
    setCurrentStep(1);
    setShowInstructions(true);
    setJsonInput('');
    setValidationResults(null);
    setSelectedSessions([]);
    setSelectedCrews([]);
    setEditingSessions({});
    setCopySuccess(false);
    setSessionCrewOverrides({});
    setConflictResults(null);
    setValidationErrors({});
  };

  const validateSessionField = (field, value, sessionIndex) => {
    const errors = [];

    switch (field) {
      case 'title':
        if (!value || value.trim().length === 0) {
          errors.push('Title is required');
        } else if (value.length > 100) {
          errors.push('Title must be less than 100 characters');
        }
        break;
      case 'date':
        if (!value) {
          errors.push('Date is required');
        } else {
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
        }
        break;
      case 'duration':
        const duration = parseInt(value);
        if (!value || isNaN(duration)) {
          errors.push('Duration is required');
        } else if (duration < 15 || duration > 480) {
          errors.push('Duration must be between 15 and 480 minutes');
        }
        break;
      case 'description':
        if (value && value.length > 500) {
          errors.push('Description must be less than 500 characters');
        }
        break;
    }

    setValidationErrors((prev) => ({
      ...prev,
      [`${sessionIndex}_${field}`]: errors,
    }));

    return errors.length === 0;
  };

  const validateJsonInput = (jsonText) => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!parsed.sessions || !Array.isArray(parsed.sessions)) {
        return { valid: false, error: 'JSON must contain a "sessions" array', line: null };
      }
      return { valid: true, data: parsed };
    } catch (error) {
      // Try to extract line number from error message
      const lineMatch = error.message.match(/line (\d+)/i);
      const line = lineMatch ? parseInt(lineMatch[1]) : null;
      return { valid: false, error: error.message, line };
    }
  };

  const handleValidateJSON = () => {
    const validation = validateJsonInput(jsonInput);
    if (!validation.valid) {
      const errorMessage = validation.line
        ? `JSON parsing error at line ${validation.line}: ${validation.error}`
        : `Invalid JSON format: ${validation.error}`;
      showError(errorMessage);
      return;
    }

    validateImportMutation.mutate(validation.data);
  };

  const handleSessionToggle = (index) => {
    setSelectedSessions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleSkipSession = (sessionIndex) => {
    // Uncheck the session when skipping
    setSelectedSessions((prev) => prev.filter((i) => i !== sessionIndex));
  };

  const revalidateSessionsAfterEdit = async () => {
    if (!validationResults) return;

    try {
      // Use the ref to get the most current editing sessions state
      const currentEditingSessions = editingSessionsRef.current;

      // Create index mapping to preserve original session indices
      const allOriginalSessions = [
        ...validationResults.valid_sessions,
        ...validationResults.invalid_sessions,
      ];
      const indexMapping = new Map();

      console.log('DEBUG: Before revalidation');
      console.log(
        'Original sessions:',
        allOriginalSessions.map((s) => ({ index: s.index, title: s.original_data.title }))
      );
      console.log('Current edits (from ref):', currentEditingSessions);
      console.log('Selected sessions:', selectedSessions);

      // Build updated sessions data with current edits and create mapping
      const updatedSessions = allOriginalSessions.map((session, arrayIndex) => {
        indexMapping.set(arrayIndex, session.index);
        const sessionWithEdits = {
          ...session.original_data,
          ...(currentEditingSessions[session.index] || {}),
        };
        console.log(`DEBUG: Session ${session.index} (array ${arrayIndex}):`, sessionWithEdits);
        return sessionWithEdits;
      });

      console.log('DEBUG: Index mapping:', Array.from(indexMapping.entries()));

      const revalidationData = { sessions: updatedSessions };
      const results = await trainingSessionsAPI.validateImportData(revalidationData, selectedCrews);

      console.log('DEBUG: Backend results:', results);

      // Restore original indices and preserve user edits
      const preservedResults = {
        ...results,
        valid_sessions: results.valid_sessions.map((session) => {
          const originalIndex = indexMapping.get(session.index);
          console.log(`DEBUG: Mapping backend index ${session.index} to original ${originalIndex}`);
          return {
            ...session,
            index: originalIndex,
            original_data: {
              ...session.original_data,
              ...(currentEditingSessions[originalIndex] || {}),
            },
          };
        }),
        invalid_sessions: results.invalid_sessions.map((session) => {
          const originalIndex = indexMapping.get(session.index);
          console.log(
            `DEBUG: Mapping invalid backend index ${session.index} to original ${originalIndex}`
          );
          return {
            ...session,
            index: originalIndex,
            original_data: {
              ...session.original_data,
              ...(currentEditingSessions[originalIndex] || {}),
            },
          };
        }),
      };

      console.log('DEBUG: Final preserved results:', preservedResults);

      // Update validation results while preserving current selection state
      setValidationResults(preservedResults);
      setConflictResults(preservedResults);
    } catch (error) {
      console.warn('Re-validation failed:', error);
      // Don't show error to user for background re-validation
    }
  };

  const handleEditSession = (index, field, value) => {
    console.log(
      `DEBUG: handleEditSession called with index=${index}, field=${field}, value=${value}`
    );

    setEditingSessions((prev) => {
      const newState = {
        ...prev,
        [index]: {
          ...prev[index],
          [field]: value,
        },
      };
      console.log(`DEBUG: Updated editingSessions state:`, newState);

      // Update the ref with the latest state
      editingSessionsRef.current = newState;

      // Re-validate sessions for conflicts if changing key fields
      if (['date', 'title', 'duration', 'description'].includes(field)) {
        // Debounce the re-validation to avoid too many API calls
        clearTimeout(revalidationTimeoutRef.current);
        revalidationTimeoutRef.current = setTimeout(() => {
          console.log(`DEBUG: About to revalidate after editing ${field} on session ${index}`);
          console.log(
            `DEBUG: Current editingSessions state at revalidation time:`,
            editingSessionsRef.current
          );
          revalidateSessionsAfterEdit();
        }, 1000); // Wait 1 second after last edit
      }

      return newState;
    });

    // Validate the field locally
    validateSessionField(field, value, index);
  };

  // Helper function to get all crews from training groups
  const getAllCrews = () => {
    if (!userTrainingGroups || !Array.isArray(userTrainingGroups)) return [];
    return userTrainingGroups.flatMap((group) => group.crews || []);
  };

  // Helper function to find crew by ID and get its training group
  const findCrewById = (crewId) => {
    if (!userTrainingGroups || !Array.isArray(userTrainingGroups)) return null;

    for (const group of userTrainingGroups) {
      if (group.crews && Array.isArray(group.crews)) {
        const crew = group.crews.find((c) => Number(c.id) === Number(crewId));
        if (crew) {
          // Add the training group ID to the crew object
          return {
            ...crew,
            training_group_id: group.id,
          };
        }
      }
    }
    return null;
  };

  const handleCrewToggle = (crewId) => {
    setSelectedCrews((prev) => {
      if (prev.includes(crewId)) {
        // Remove crew
        return prev.filter((id) => id !== crewId);
      } else {
        // Add crew - check if it's from the same training group
        if (!canAddCrew(crewId)) {
          console.error('Cannot add crews from different training groups');
          return prev;
        }

        return [...prev, crewId];
      }
    });
  };

  const getCurrentTrainingGroupId = () => {
    if (selectedCrews.length === 0) return null;

    // Get the first crew's training group
    const firstCrew = findCrewById(selectedCrews[0]);
    return firstCrew?.training_group_id || null;
  };

  const canAddCrewFromGroup = (group) => {
    const currentGroupId = getCurrentTrainingGroupId();

    // If no crews assigned yet, can add from any group
    if (currentGroupId === null) return true;

    // Can only add crews from the same training group
    return Number(group.id) === Number(currentGroupId);
  };

  const canAddCrew = (crewId) => {
    const currentGroupId = getCurrentTrainingGroupId();

    // If no crews assigned yet, can add any crew
    if (currentGroupId === null) return true;

    // Check if this crew is from the same training group
    const crew = findCrewById(crewId);
    return crew && Number(crew.training_group_id) === Number(currentGroupId);
  };

  const handleBulkAssignFromGroup = (groupId) => {
    if (!userTrainingGroups || !Array.isArray(userTrainingGroups)) {
      console.error('userTrainingGroups is not available');
      return;
    }

    const group = userTrainingGroups.find((g) => g.id === groupId);
    if (group && group.crews && Array.isArray(group.crews) && group.crews.length > 0) {
      // Check if we can add crews from this group
      if (!canAddCrewFromGroup(group)) {
        console.error('Cannot add crews from different training groups');
        return;
      }

      const crewIds = group.crews.map((c) => c.id).filter((id) => id != null);
      setSelectedCrews((prev) => [...new Set([...prev, ...crewIds])]);
    } else {
      console.error('Group not found or has no crews:', groupId);
    }
  };

  const handleSessionCrewOverride = (sessionIndex, crewIds) => {
    // Check if the assignment matches the bulk assignment
    const arraysEqual = (a, b) => {
      if (a.length !== b.length) return false;
      return a.sort().join(',') === b.sort().join(',');
    };

    if (arraysEqual(crewIds, selectedCrews)) {
      // If assignment matches bulk, clear the override to use bulk assignment
      clearSessionCrewOverride(sessionIndex);
    } else {
      // Set custom assignment
      setSessionCrewOverrides((prev) => ({
        ...prev,
        [sessionIndex]: crewIds,
      }));
    }
  };

  const clearSessionCrewOverride = (sessionIndex) => {
    setSessionCrewOverrides((prev) => {
      const newOverrides = { ...prev };
      delete newOverrides[sessionIndex];
      return newOverrides;
    });
  };

  const getSessionCrews = (sessionIndex) => {
    return sessionCrewOverrides[sessionIndex] || selectedCrews;
  };

  const hasCustomCrewAssignment = (sessionIndex) => {
    return sessionCrewOverrides.hasOwnProperty(sessionIndex);
  };

  const getSessionConflicts = (sessionIndex) => {
    return (
      validationResults?.conflicts?.filter((conflict) => conflict.session_index === sessionIndex) ||
      []
    );
  };

  const unselectAllConflictingSessions = () => {
    if (!validationResults?.conflicts) return;

    // Get all session indices that have conflicts
    const conflictingIndices = [
      ...new Set(validationResults.conflicts.map((conflict) => conflict.session_index)),
    ];

    // Remove conflicting sessions from selected sessions
    setSelectedSessions((prev) => prev.filter((index) => !conflictingIndices.includes(index)));
  };

  const unselectAllPastSessions = () => {
    if (!validationResults?.valid_sessions) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get session indices that are in the past
    const pastSessionIndices = validationResults.valid_sessions
      .filter((session) => {
        const sessionData = { ...session.original_data, ...(editingSessions[session.index] || {}) };
        if (!sessionData.date) return false;

        const sessionDate = new Date(sessionData.date);
        return sessionDate < today;
      })
      .map((session) => session.index);

    // Remove past sessions from selected sessions
    setSelectedSessions((prev) => prev.filter((index) => !pastSessionIndices.includes(index)));
  };

  const addManualSession = () => {
    if (!validationResults) return;

    const newIndex = Math.max(...validationResults.valid_sessions.map((s) => s.index), -1) + 1;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const newSession = {
      index: newIndex,
      original_data: {
        title: 'New Training Session',
        description: '',
        date: tomorrow.toISOString().split('T')[0],
        duration: 90,
        location: '',
      },
      warnings: [],
    };

    setValidationResults((prev) => ({
      ...prev,
      valid_sessions: [...prev.valid_sessions, newSession],
    }));

    setSelectedSessions((prev) => [...prev, newIndex]);
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(LLMPromptTemplate);
      setCopySuccess(true);
      showSuccess('LLM prompt copied to clipboard!');
      // Reset the copy success state after 2 seconds
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      showError('Failed to copy prompt. Please try again.');
    }
  };

  const handleFinalImport = () => {
    if (selectedSessions.length === 0) {
      showError('Please select at least one session to import');
      return;
    }

    // Build final sessions data with edits and crew assignments
    const finalSessions = selectedSessions.map((index) => {
      const originalSession = validationResults.valid_sessions.find((s) => s.index === index);
      const edits = editingSessions[index] || {};
      const sessionCrews = getSessionCrews(index);

      // Ensure crew IDs are integers, not strings
      const crewAssignments = sessionCrews.map((id) => parseInt(id, 10));

      console.log('DEBUG: Session', index, 'crew assignments:', crewAssignments);

      return {
        ...originalSession.original_data,
        ...edits,
        crew_assignments: crewAssignments,
      };
    });

    // Ensure default crew assignments are also integers
    const defaultCrewAssignments = selectedCrews.map((id) => parseInt(id, 10));

    const importRequest = {
      sessions: finalSessions,
      default_crew_assignments: defaultCrewAssignments, // Fallback for sessions without specific assignments
    };

    console.log('DEBUG: Final import request:', importRequest);

    bulkCreateMutation.mutate(importRequest);
  };

  if (!isOpen) return null;

  // Early return if critical data is not available
  if (currentStep === 2 && (!userTrainingGroups || !Array.isArray(userTrainingGroups))) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white">
          <div className="flex items-center justify-between border-b border-gray-200 p-6">
            <h2 className="text-2xl font-bold text-gray-900">Import Training Sessions</h2>
            <button
              onClick={onClose}
              className="text-gray-400 transition-colors hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <div className="text-gray-600">Loading training groups...</div>
          </div>
        </div>
      </div>
    );
  }

  const LLMPromptTemplate = `Analyze this rowing training schedule image and extract all training sessions into a structured JSON format.

Instructions:
- Extract only training sessions which are done with crews
- Only extract those that are visible in the schedule
- Use the exact format provided below
- For dates, use YYYY-MM-DD format
- If no specific time is mentioned, use "TBD"
- Extract duration from the schema, if not specified, reason about it based on the session title and description.
- Include all relevant details from the image
- Do not invent information about the ease or impact of the session, only extract what is explicitly stated except for the duration.
- Information like "ED or AT" combined with distance indicators should be included in the title.
- Any extra indicators about the session contents should be in the description.
- Location should be included if specified, otherwise leave it empty.

Required JSON format:
{
  "sessions": [
    {
      "title": "Morning Water Training",
      "description": "Technical focus on blade work",
      "date": "2025-06-28",
      "duration": "90",
      "location": "Main boathouse"
    }
  ]
}

Important: Only extract the training sessions that are on the water, not ergs or other types.

Important: Only include actual training sessions, not general notes or headers. Return only the JSON, no additional text.`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-2xl font-bold text-gray-900">Import Training Sessions</h2>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Left Panel - Instructions (collapsible) */}
          {showInstructions && (
            <div className="w-1/3 overflow-y-auto border-r border-gray-200 bg-gray-50 p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">How to Use</h3>
                <button
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="mb-2 font-medium text-gray-900">
                    Step 1: External LLM Processing
                  </h4>
                  <ol className="list-inside list-decimal space-y-1 text-gray-600">
                    <li>Take a clear screenshot of your training schedule</li>
                    <li>Go to ChatGPT, Claude, or Gemini</li>
                    <li>Upload your screenshot</li>
                    <li>Copy the prompt below</li>
                    <li>Paste it into the LLM chat</li>
                    <li>Run the prompt to extract training sessions</li>
                    <li>Copy the JSON response and paste it on the right</li>
                  </ol>
                </div>

                <div>
                  <h4 className="mb-2 font-medium text-gray-900">LLM Prompt (Click to Copy)</h4>
                  <div
                    className={`cursor-pointer rounded border bg-white p-3 font-mono text-xs transition-colors hover:bg-gray-50 ${
                      copySuccess ? 'border-green-300 bg-green-50' : ''
                    }`}
                    onClick={handleCopyPrompt}
                    title="Click to copy prompt"
                  >
                    {LLMPromptTemplate.substring(0, 200)}...
                    <div className={`mt-1 ${copySuccess ? 'text-green-600' : 'text-blue-600'}`}>
                      {copySuccess ? '✓ Copied to clipboard!' : 'Click to copy full prompt'}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-medium text-gray-900">Step 2: Assign Crews</h4>
                  <p className="text-gray-600">
                    Select which crews to assign to all imported sessions.
                  </p>
                </div>

                <div>
                  <h4 className="mb-2 font-medium text-gray-900">Step 3: Review & Edit Sessions</h4>
                  <p className="text-gray-600">
                    Review, edit, and customize individual sessions before final import.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Panel */}
          <div className={`${showInstructions ? 'w-2/3' : 'w-full'} flex min-h-0 flex-col`}>
            {currentStep === 1 && (
              <div className="flex min-h-0 flex-1 flex-col p-6">
                <h3 className="mb-4 text-lg font-semibold">Paste LLM JSON Output</h3>

                <textarea
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  placeholder="Paste the JSON output from your LLM here..."
                  className="w-full flex-1 resize-none rounded-lg border border-gray-300 p-4 font-mono text-sm"
                />

                {/* JSON Validation Feedback */}
                {jsonInput && (
                  <div className="mt-2">
                    {(() => {
                      const validation = validateJsonInput(jsonInput);
                      if (!validation.valid) {
                        return (
                          <div className="rounded bg-red-50 p-2 text-sm text-red-600">
                            <strong>JSON Error:</strong> {validation.error}
                            {validation.line && ` (Line ${validation.line})`}
                          </div>
                        );
                      } else if (validation.data) {
                        const sessionCount = validation.data.sessions?.length || 0;
                        return (
                          <div className="rounded bg-green-50 p-2 text-sm text-green-600">
                            ✓ Valid JSON with {sessionCount} session{sessionCount !== 1 ? 's' : ''}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setJsonInput(
                          JSON.stringify(
                            {
                              sessions: [
                                {
                                  title: 'Morning Water Training',
                                  description: 'Technical focus on blade work',
                                  date: '2025-06-28',
                                  duration: '90',
                                  location: 'Main boathouse',
                                },
                              ],
                            },
                            null,
                            2
                          )
                        )
                      }
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Load Example JSON
                    </button>
                    <button
                      onClick={() => {
                        try {
                          const parsed = JSON.parse(jsonInput);
                          setJsonInput(JSON.stringify(parsed, null, 2));
                          showSuccess('JSON formatted successfully');
                        } catch (error) {
                          showError('Cannot format invalid JSON');
                        }
                      }}
                      disabled={!jsonInput.trim()}
                      className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                    >
                      Format JSON
                    </button>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setJsonInput('')}
                      className="px-4 py-2 text-gray-600 transition-colors hover:text-gray-800"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => {
                        // Only go to step 2 if JSON is valid
                        const validation = validateJsonInput(jsonInput);
                        if (validation.valid) {
                          setCurrentStep(2);
                        } else {
                          showError('Please fix JSON errors before continuing.');
                        }
                      }}
                      disabled={!jsonInput.trim()}
                      className="rounded-lg bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700 disabled:bg-gray-300"
                    >
                      Continue to Crew Assignment
                    </button>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="border-b border-gray-200 p-6">
                  <h3 className="mb-4 text-lg font-semibold">Assign Crews to Sessions</h3>
                  <p className="mb-4 text-gray-600">
                    Select crews to assign to all {selectedSessions.length} validated sessions. You
                    can modify individual assignments in the next step.
                  </p>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      ← Back to JSON Input
                    </button>
                    <button
                      onClick={() => handleValidateJSON()}
                      disabled={selectedCrews.length === 0}
                      className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 disabled:bg-gray-300"
                    >
                      Continue to Session Review
                    </button>
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-6">
                  {loadingTrainingGroups ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-gray-600">Loading training groups...</div>
                    </div>
                  ) : trainingGroupsError ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                      <div className="text-red-700">
                        <strong>Error loading training groups:</strong>{' '}
                        {trainingGroupsError.message}
                      </div>
                    </div>
                  ) : userTrainingGroups.length === 0 ? (
                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                      <div className="text-yellow-700">
                        <strong>No training groups found.</strong> You need to be a member of at
                        least one training group to import sessions.
                      </div>
                    </div>
                  ) : (
                    <CrewAssignmentPanel
                      userTrainingGroups={userTrainingGroups}
                      selectedCrews={selectedCrews}
                      onCrewToggle={handleCrewToggle}
                      onBulkAssignFromGroup={handleBulkAssignFromGroup}
                      canAddCrewFromGroup={canAddCrewFromGroup}
                      canAddCrew={canAddCrew}
                      getCurrentTrainingGroupId={getCurrentTrainingGroupId}
                    />
                  )}
                </div>
              </div>
            )}

            {currentStep === 3 && validationResults && (
              <div className="flex min-h-0 flex-1 flex-col">
                {/* Validation Summary */}
                <div className="border-b border-gray-200 p-6">
                  <div className="mb-4 flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-700">
                        {validationResults.valid_sessions.length} valid sessions
                      </span>
                    </div>{' '}
                    {validationResults.invalid_sessions.length > 0 && (
                      <div className="flex items-center gap-2">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                        <span className="text-sm text-red-700">
                          {validationResults.invalid_sessions.length} sessions with errors
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Import Summary */}
                  <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h4 className="mb-2 font-medium text-blue-900">Import Summary</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-blue-700">Sessions to Import:</span>
                        <span className="ml-2 font-medium">{selectedSessions.length}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Default Crews:</span>
                        <span className="ml-2 font-medium">{selectedCrews.length}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">Custom Assignments:</span>
                        <span className="ml-2 font-medium">
                          {Object.keys(sessionCrewOverrides).length} sessions
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    {/* Navigation */}
                    <div className="flex justify-start">
                      <button
                        onClick={() => setCurrentStep(2)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ← Back to Crew Assignment
                      </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2 sm:flex-row">
                      {/* Bulk Actions */}
                      <div className="flex flex-1 flex-col gap-2 sm:flex-row">
                        {validationResults?.conflicts?.length > 0 && (
                          <button
                            onClick={unselectAllConflictingSessions}
                            className="rounded-lg border border-yellow-600 px-3 py-2 text-sm text-yellow-600 hover:bg-yellow-50"
                            title="Unselect all sessions that have scheduling conflicts"
                          >
                            🚫 Skip Conflicting
                          </button>
                        )}
                        <button
                          onClick={unselectAllPastSessions}
                          className="rounded-lg border border-orange-600 px-3 py-2 text-sm text-orange-600 hover:bg-orange-50"
                          title="Unselect all sessions that are in the past"
                        >
                          📅 Skip Past Sessions
                        </button>
                        <button
                          onClick={addManualSession}
                          className="rounded-lg border border-blue-600 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50"
                        >
                          ➕ Add Session
                        </button>
                      </div>

                      {/* Import Action */}
                      <button
                        onClick={handleFinalImport}
                        disabled={bulkCreateMutation.isLoading || selectedSessions.length === 0}
                        className="whitespace-nowrap rounded-lg bg-green-600 px-6 py-2 text-white hover:bg-green-700 disabled:bg-gray-300"
                      >
                        {bulkCreateMutation.isLoading
                          ? 'Creating Sessions...'
                          : `Import ${selectedSessions.length} Sessions`}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sessions List */}
                <div className="min-h-0 flex-1 overflow-y-auto p-6">
                  <div className="space-y-4">
                    {validationResults.valid_sessions.map((session) => (
                      <SessionPreviewCard
                        key={session.index}
                        session={session}
                        isSelected={selectedSessions.includes(session.index)}
                        onToggle={() => handleSessionToggle(session.index)}
                        onEdit={handleEditSession}
                        edits={editingSessions[session.index] || {}}
                        validationErrors={validationErrors}
                        userTrainingGroups={userTrainingGroups}
                        bulkCrews={selectedCrews}
                        sessionCrews={getSessionCrews(session.index)}
                        onCrewOverride={handleSessionCrewOverride}
                        hasCustomCrews={hasCustomCrewAssignment(session.index)}
                        onClearCrewOverride={clearSessionCrewOverride}
                        conflicts={getSessionConflicts(session.index)}
                        onSkip={() => handleSkipSession(session.index)}
                      />
                    ))}

                    {validationResults.invalid_sessions.map((session) => (
                      <div
                        key={session.index}
                        className="rounded-lg border border-red-200 bg-red-50 p-4"
                      >
                        <div className="mb-2 flex items-center gap-2">
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                          <span className="font-medium text-red-900">
                            {session.original_data.title || 'Untitled Session'}
                          </span>
                        </div>
                        <ul className="list-inside list-disc text-sm text-red-700">
                          {session.errors.map((error, i) => (
                            <li key={i}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Session Preview Card Component
const SessionPreviewCard = ({
  session,
  isSelected,
  onToggle,
  onEdit,
  edits,
  validationErrors,
  userTrainingGroups,
  bulkCrews,
  sessionCrews,
  onCrewOverride,
  hasCustomCrews,
  onClearCrewOverride,
  conflicts,
  onSkip,
}) => {
  const sessionData = { ...session.original_data, ...edits };
  const [showCrewEditor, setShowCrewEditor] = useState(false);

  const getFieldError = (field) => {
    return validationErrors[`${session.index}_${field}`] || [];
  };

  const hasErrors = Object.keys(validationErrors).some(
    (key) => key.startsWith(`${session.index}_`) && validationErrors[key].length > 0
  );

  return (
    <div
      className={`rounded-lg border p-4 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'} ${hasErrors ? 'border-red-300' : ''}`}
    >
      <div className="flex items-start gap-3">
        <input type="checkbox" checked={isSelected} onChange={onToggle} className="mt-1" />
        <div className="flex-1 space-y-2">
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Session #{session.index}</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700">Title</label>
              <input
                type="text"
                value={sessionData.title || ''}
                onChange={(e) => onEdit(session.index, 'title', e.target.value)}
                className={`w-full rounded border px-2 py-1 text-sm ${getFieldError('title').length > 0 ? 'border-red-300' : 'border-gray-300'}`}
              />
              {getFieldError('title').length > 0 && (
                <div className="mt-1 text-xs text-red-600">{getFieldError('title').join(', ')}</div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">Date</label>
              <input
                type="date"
                value={sessionData.date || ''}
                onChange={(e) => onEdit(session.index, 'date', e.target.value)}
                className={`w-full rounded border px-2 py-1 text-sm ${getFieldError('date').length > 0 ? 'border-red-300' : 'border-gray-300'}`}
              />
              {getFieldError('date').length > 0 && (
                <div className="mt-1 text-xs text-red-600">{getFieldError('date').join(', ')}</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700">Duration (min)</label>
              <input
                type="number"
                min="15"
                max="480"
                value={sessionData.duration}
                onChange={(e) => onEdit(session.index, 'duration', e.target.value)}
                className={`w-full rounded border px-2 py-1 text-sm ${getFieldError('duration').length > 0 ? 'border-red-300' : 'border-gray-300'}`}
              />
              {getFieldError('duration').length > 0 && (
                <div className="mt-1 text-xs text-red-600">
                  {getFieldError('duration').join(', ')}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">Location</label>
              <input
                type="text"
                value={sessionData.location || ''}
                onChange={(e) => onEdit(session.index, 'location', e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                placeholder="Training location..."
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700">Description</label>
            <textarea
              value={sessionData.description || ''}
              onChange={(e) => onEdit(session.index, 'description', e.target.value)}
              className={`h-16 w-full rounded border px-2 py-1 text-sm ${getFieldError('description').length > 0 ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="Session description..."
            />
            {getFieldError('description').length > 0 && (
              <div className="mt-1 text-xs text-red-600">
                {getFieldError('description').join(', ')}
              </div>
            )}
          </div>

          {/* Crew Assignment Section */}
          <div className="border-t pt-2">
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700">Crew Assignment</label>
              <button
                onClick={() => setShowCrewEditor(!showCrewEditor)}
                className={`rounded px-2 py-1 text-xs ${hasCustomCrews ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}
              >
                {hasCustomCrews ? 'Custom Assignment' : 'Using Bulk Assignment'}
              </button>
            </div>

            {hasCustomCrews ? (
              <div className="mb-2 text-xs text-orange-700">
                {sessionCrews.length} crews assigned (custom)
                <button
                  onClick={() => onClearCrewOverride(session.index)}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  Use Bulk Assignment
                </button>
              </div>
            ) : (
              <div className="mb-2 text-xs text-gray-600">
                {bulkCrews.length} crews from bulk assignment
              </div>
            )}

            {showCrewEditor && (
              <SessionCrewEditor
                sessionIndex={session.index}
                userTrainingGroups={userTrainingGroups || []}
                selectedCrews={sessionCrews}
                onCrewChange={onCrewOverride}
                onClose={() => setShowCrewEditor(false)}
              />
            )}
          </div>

          {/* Conflicts Section */}
          {conflicts && conflicts.length > 0 && (
            <div className="border-t pt-2">
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <div className="mb-2 flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-900">
                    Scheduling Conflicts Detected
                  </span>
                </div>
                {conflicts.map((conflict, index) => {
                  const conflictDate = new Date(conflict.existing_session?.date);
                  const formattedDate = conflictDate.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  });

                  return (
                    <div key={index} className="mb-2 last:mb-0">
                      <div className="text-xs text-yellow-700">
                        <p>
                          <strong>Conflicts with:</strong> {conflict.existing_session?.title} on{' '}
                          {formattedDate}
                        </p>
                        {conflict.reason && (
                          <p>
                            <strong>Reason:</strong> {conflict.reason}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div className="mt-2">
                  <button
                    onClick={onSkip}
                    className="rounded bg-gray-600 px-2 py-1 text-xs text-white hover:bg-gray-700"
                    title="Skip this session (uncheck it)"
                  >
                    Skip This Session
                  </button>
                </div>
              </div>
            </div>
          )}

          {session.warnings && session.warnings.length > 0 && (
            <div className="rounded bg-yellow-50 p-3 text-xs text-yellow-700">
              <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />{' '}
              {session.warnings.join(', ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Session Crew Editor Component
const SessionCrewEditor = ({
  sessionIndex,
  userTrainingGroups,
  selectedCrews,
  onCrewChange,
  onClose,
}) => {
  const handleCrewToggle = (crewId) => {
    const newSelection = selectedCrews.includes(crewId)
      ? selectedCrews.filter((id) => id !== crewId)
      : [...selectedCrews, crewId];

    // Immediately apply the change
    onCrewChange(sessionIndex, newSelection);
  };

  const handleGroupSelect = (groupId) => {
    if (!userTrainingGroups || !Array.isArray(userTrainingGroups)) {
      console.error('userTrainingGroups is not available');
      return;
    }

    const group = userTrainingGroups.find((g) => g.id === groupId);
    if (group && group.crews && Array.isArray(group.crews)) {
      const groupCrewIds = group.crews.map((c) => c.id).filter((id) => id != null);
      const newSelection = [...new Set([...selectedCrews, ...groupCrewIds])];

      // Immediately apply the change
      onCrewChange(sessionIndex, newSelection);
    } else {
      console.error('Group not found or has no crews:', groupId);
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-gray-300 bg-white p-3">
      <div className="mb-3 flex items-center justify-between">
        <h5 className="font-medium text-gray-900">Customize Crew Assignment</h5>
        <button onClick={onClose} className="text-xs text-gray-600 hover:text-gray-800">
          ✕ Close
        </button>
      </div>

      <div className="space-y-3">
        {userTrainingGroups && Array.isArray(userTrainingGroups) ? (
          userTrainingGroups.map((group) => {
            if (!group || !group.id || !group.name) {
              return (
                <div
                  key={group?.id || 'invalid'}
                  className="rounded border border-red-200 bg-red-50 p-2"
                >
                  <div className="text-xs text-red-700">Invalid group data</div>
                </div>
              );
            }

            return (
              <div key={group.id} className="rounded border border-gray-200 p-2">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700">{group.name}</span>
                  {group.crews && group.crews.length > 0 && (
                    <button
                      onClick={() => handleGroupSelect(group.id)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Add All
                    </button>
                  )}
                </div>
                {group.crews && group.crews.length > 0 ? (
                  <div className="grid grid-cols-2 gap-1">
                    {group.crews.map((crew) => {
                      if (!crew || !crew.id || !crew.name) {
                        return (
                          <div key={crew?.id || 'invalid'} className="text-xs text-red-600">
                            Invalid crew data
                          </div>
                        );
                      }

                      return (
                        <label key={crew.id} className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={selectedCrews.includes(crew.id)}
                            onChange={() => handleCrewToggle(crew.id)}
                            className="scale-75"
                          />
                          <span>{crew.name}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs italic text-gray-500">No crews available</div>
                )}
              </div>
            );
          })
        ) : (
          <div className="rounded border border-red-200 bg-red-50 p-2">
            <div className="text-xs text-red-700">No training groups available</div>
          </div>
        )}

        <div className="text-xs text-gray-600">
          {selectedCrews.length} crews selected for this session
        </div>
      </div>
    </div>
  );
};

// Crew Assignment Panel Component
const CrewAssignmentPanel = ({
  userTrainingGroups,
  selectedCrews,
  onCrewToggle,
  onBulkAssignFromGroup,
  canAddCrewFromGroup,
  canAddCrew,
  getCurrentTrainingGroupId,
}) => {
  // Validate props
  if (!userTrainingGroups || !Array.isArray(userTrainingGroups)) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="text-red-700">
          <strong>Error:</strong> Invalid training groups data
        </div>
      </div>
    );
  }

  if (!selectedCrews || !Array.isArray(selectedCrews)) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="text-red-700">
          <strong>Error:</strong> Invalid selected crews data
        </div>
      </div>
    );
  }

  if (!onCrewToggle || typeof onCrewToggle !== 'function') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="text-red-700">
          <strong>Error:</strong> Invalid crew toggle handler
        </div>
      </div>
    );
  }

  if (!onBulkAssignFromGroup || typeof onBulkAssignFromGroup !== 'function') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="text-red-700">
          <strong>Error:</strong> Invalid bulk assign handler
        </div>
      </div>
    );
  }

  if (!canAddCrew || typeof canAddCrew !== 'function') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="text-red-700">
          <strong>Error:</strong> Invalid canAddCrew handler
        </div>
      </div>
    );
  }

  if (!getCurrentTrainingGroupId || typeof getCurrentTrainingGroupId !== 'function') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="text-red-700">
          <strong>Error:</strong> Invalid getCurrentTrainingGroupId handler
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Training Group Selection Info */}
      {getCurrentTrainingGroupId() && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="text-sm text-blue-800">
            <strong>Note:</strong> Only crews from the same training group as the selected crew can
            be added.
          </div>
        </div>
      )}

      {/* Training Groups */}
      {userTrainingGroups.map((group) => {
        // Validate group structure
        if (!group || !group.id || !group.name) {
          return (
            <div
              key={group?.id || 'invalid'}
              className="rounded-lg border border-red-200 bg-red-50 p-4"
            >
              <div className="text-red-700">
                <strong>Error:</strong> Invalid group data structure
              </div>
            </div>
          );
        }

        return (
          <div key={group.id} className="rounded-lg border border-gray-200 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="font-medium text-gray-900">{group.name}</h4>
              {group.crews && group.crews.length > 0 && (
                <button
                  onClick={() => onBulkAssignFromGroup(group.id)}
                  className={`text-sm ${
                    canAddCrewFromGroup && canAddCrewFromGroup(group)
                      ? 'text-blue-600 hover:text-blue-800'
                      : 'cursor-not-allowed text-gray-400'
                  }`}
                  disabled={!canAddCrewFromGroup || !canAddCrewFromGroup(group)}
                >
                  Add All Crews
                </button>
              )}
            </div>

            {group.crews && group.crews.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {group.crews.map((crew) => {
                  // Validate crew structure
                  if (!crew || !crew.id || !crew.name) {
                    return (
                      <div key={crew?.id || 'invalid'} className="text-sm text-red-600">
                        Invalid crew data
                      </div>
                    );
                  }

                  const canAdd = canAddCrew(crew.id);

                  return (
                    <label
                      key={crew.id}
                      className={`flex items-center gap-2 rounded p-2 text-sm ${
                        !canAdd ? 'bg-gray-100 opacity-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCrews.includes(crew.id)}
                        onChange={() => onCrewToggle(crew.id)}
                        disabled={!canAdd}
                        style={{ pointerEvents: !canAdd ? 'none' : 'auto' }}
                      />
                      <span className={!canAdd ? 'text-gray-400' : ''}>{crew.name}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm italic text-gray-500">
                No crews available in this training group
              </div>
            )}
          </div>
        );
      })}

      {selectedCrews.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h4 className="mb-2 font-medium text-blue-900">
            Selected Crews ({selectedCrews.length})
          </h4>
          <p className="text-sm text-blue-700">
            These crews will be assigned to all imported training sessions.
          </p>
        </div>
      )}
    </div>
  );
};

export default ImportSessionsModal;
